// src/App.js
import { Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { APP_VERSION } from "./version";
import { Suspense, useEffect, useState, lazy } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { BrandProvider } from "./context/BrandContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import StaffBrand from "./components/StaffBrand";
import StaffLoading from "./components/StaffLoading";
import "./staffWorkspace.css";
import Login from "./pages/Login";
import { firebaseEmulatorsEnabled, customerPortalEnabled, callAppPortalFunction } from "./firebase";
import { useAppUsage } from "./utils/useAppUsage";
import { signInClientHints } from "./utils/signInLog";
import { portalDomainRedirect } from "./utils/portalNavigation";

console.log("App.jsx loading - importing lazy components");

// Lazy load all page components to avoid circular dependencies
const Dashboard = lazy(() => import("./pages/Dashboard").catch(e => { console.error("Dashboard load error:", e); throw e; }));
const Customers = lazy(() => import("./pages/Customers").catch(e => { console.error("Customers load error:", e); throw e; }));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail").catch(e => { console.error("CustomerDetail load error:", e); throw e; }));
const Repairs = lazy(() => import("./pages/Repairs").catch(e => { console.error("Repairs load error:", e); throw e; }));
const Lines = lazy(() => import("./pages/Lines").catch(e => { console.error("Lines load error:", e); throw e; }));
const ServiceRequests = lazy(() => import("./pages/ServiceRequests"));
const TravelEsims = lazy(() => import("./pages/TravelEsims"));
const TravelInstall = lazy(() => import("./pages/TravelInstall"));
const PlanTemplates = lazy(() => import("./pages/PlanTemplates").catch(e => { console.error("PlanTemplates load error:", e); throw e; }));
const Reminders = lazy(() => import("./pages/Reminders").catch(e => { console.error("Reminders load error:", e); throw e; }));
const Inventory = lazy(() => import("./pages/Inventory").catch(e => { console.error("Inventory load error:", e); throw e; }));
const Payments = lazy(() => import("./pages/Payments").catch(e => { console.error("Payments load error:", e); throw e; }));
const Shipments = lazy(() => import("./pages/Shipments"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders").catch(e => { console.error("PurchaseOrders load error:", e); throw e; }));
const FinancialReport = lazy(() => import("./pages/FinancialReport").catch(e => { console.error("FinancialReport load error:", e); throw e; }));
const Messages = lazy(() => import("./pages/Messages").catch(e => { console.error("Messages load error:", e); throw e; }));
const CrmInbox = lazy(() => import("./pages/CrmInbox"));
const VendorInventory = lazy(() => import("./pages/VendorInventory").catch(e => { console.error("VendorInventory load error:", e); throw e; }));
const MobileSentrix = lazy(() => import("./pages/MobileSentrix").catch(e => { console.error("MobileSentrix load error:", e); throw e; }));
const PartsInventory = lazy(() => import("./pages/PartsInventory").catch(e => { console.error("PartsInventory load error:", e); throw e; }));
const Finance = lazy(() => import("./pages/Finance").catch(e => { console.error("Finance load error:", e); throw e; }));
const Quotes = lazy(() => import("./pages/Quotes"));
const RewardsEnhanced = lazy(() => import("./pages/RewardsEnhanced").catch(e => { console.error("RewardsEnhanced load error:", e); throw e; }));
const Promotions = lazy(() => import("./pages/Promotions").catch(e => { console.error("Promotions load error:", e); throw e; }));
const Profitability = lazy(() => import("./pages/Profitability").catch(e => { console.error("Profitability load error:", e); throw e; }));
const Collections = lazy(() => import("./pages/Collections").catch(e => { console.error("Collections load error:", e); throw e; }));
const FinancialLedger = lazy(() => import("./pages/FinancialLedger").catch(e => { console.error("FinancialLedger load error:", e); throw e; }));
const CarrierCosts = lazy(() => import("./pages/CarrierCosts").catch(e => { console.error("CarrierCosts load error:", e); throw e; }));
const AdminCenter = lazy(() => import("./pages/AdminCenter").catch(e => { console.error("AdminCenter load error:", e); throw e; }));
const Account = lazy(() => import("./pages/Account").catch(e => { console.error("Account load error:", e); throw e; }));
const PublicInventory = lazy(() => import("./pages/PublicInventory").catch(e => { console.error("PublicInventory load error:", e); throw e; }));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const Ledger = lazy(() => import("./pages/Ledger").catch(e => { console.error("Ledger load error:", e); throw e; }));

const LoadingFallback = ({ publicPage = false }) => publicPage
  ? <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
  : <StaffLoading fullScreen={false} />;

function LegacyAdminRedirect({ branding = false }) {
  const { hash } = useLocation();
  return <Navigate to={`/admin${branding ? "#branding" : hash}`} replace />;
}

function AppLayout() {
  const { user, allowed, logout } = useAuth();
  const usageError = useAppUsage({ identity: allowed ? user?.uid : null, kind: "EMPLOYEE", call: callAppPortalFunction });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(() =>
    typeof navigator === "undefined" ? false : !navigator.onLine
  );

  useEffect(() => {
    if (!allowed || !user?.uid) return;
    // The server records one entry per Firebase sign-in (auth_time), so reloads are not duplicated.
    callAppPortalFunction("staffSignInRecord", { client: signInClientHints() })
      .catch(error => console.error("Unable to record staff sign-in:", error));
  }, [allowed, user?.uid, user?.metadata?.lastSignInTime]);

  useEffect(() => {
    if (!allowed || !user) return undefined;
    const idleLimit = 60 * 60 * 1000;
    const activityKey = `alkeya_staff_activity_${user.uid}`;
    const signInTime = user.metadata?.lastSignInTime || "";
    const readActivity = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(activityKey) || "null");
        return saved?.signInTime === signInTime && Number.isFinite(saved.lastActivity) ? saved.lastActivity : null;
      } catch (error) {
        console.error("Unable to read staff activity timestamp:", error);
        return null;
      }
    };
    let lastActivity = readActivity() ?? Date.now();
    let timer;
    const saveActivity = () => {
      try {
        localStorage.setItem(activityKey, JSON.stringify({ signInTime, lastActivity }));
      } catch (error) {
        console.error("Unable to save staff activity timestamp:", error);
      }
    };
    const signOutWhenIdle = () => {
      if (Date.now() - lastActivity < idleLimit) {
        scheduleSignOut();
        return;
      }
      logout().catch(error => console.error("Unable to sign out after staff inactivity:", error));
    };
    const scheduleSignOut = () => {
      clearTimeout(timer);
      timer = setTimeout(signOutWhenIdle, Math.max(0, idleLimit - (Date.now() - lastActivity)));
    };
    const recordActivity = () => {
      const now = Date.now();
      if (now - lastActivity < 1000) return;
      lastActivity = now;
      saveActivity();
      scheduleSignOut();
    };
    const checkActivity = () => {
      lastActivity = Math.max(lastActivity, readActivity() ?? 0);
      if (Date.now() - lastActivity >= idleLimit) signOutWhenIdle();
      else scheduleSignOut();
    };
    const syncActivity = event => {
      if (event.key === activityKey) checkActivity();
    };
    const activityEvents = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel", "scroll"];
    if (readActivity() === null) saveActivity();
    activityEvents.forEach(event => window.addEventListener(event, recordActivity, { passive: true }));
    window.addEventListener("focus", checkActivity);
    window.addEventListener("storage", syncActivity);
    scheduleSignOut();
    return () => {
      clearTimeout(timer);
      activityEvents.forEach(event => window.removeEventListener(event, recordActivity));
      window.removeEventListener("focus", checkActivity);
      window.removeEventListener("storage", syncActivity);
    };
  }, [allowed, logout, user]);

  useEffect(() => {
    const updateConnection = () => setIsOffline(!navigator.onLine);
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);
    return () => {
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileOpen]);

  // Hydrate brand variables on first render (for consistent tile colors)
  useEffect(() => {
    console.log("AppLayout mounted");
    try {
      const raw = localStorage.getItem("brandVars");
      if (!raw) return;
      const v = JSON.parse(raw);
      const root = document.documentElement;
      if (!root) return;
      if (v.surfaceLight) root.style.setProperty("--brand-surface-light", v.surfaceLight);
      if (v.borderLight) root.style.setProperty("--brand-border-light", v.borderLight);
      if (v.surfaceDark) root.style.setProperty("--brand-surface-dark", v.surfaceDark);
      if (v.borderDark) root.style.setProperty("--brand-border-dark", v.borderDark);
    } catch (e) {
      console.error("Brand vars error:", e);
    }
  }, []);

  return (
    <div
      className="app-shell staff-polish min-h-screen min-h-screen-fix overflow-x-hidden bg-gray-50 text-gray-900 dark:bg-[color:var(--brand-bg-dark)] dark:text-gray-100"
    >
      {/* Mobile top bar */}
      <div className="mobile-topbar lg:hidden sticky top-0 z-40 bg-white/80 dark:bg-gray-900/90 backdrop-blur border-b dark:border-gray-700 safe-top">
        <div className="flex items-center justify-between px-4 py-3 safe-left safe-right">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
            aria-label="Open navigation"
          >
            ☰ Menu
          </button>
          <StaffBrand />
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded border dark:border-gray-700 bg-white/70 dark:bg-gray-800">
              {APP_VERSION}
            </span>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <Sidebar variant="desktop" />

        {/* Main content */}
        <main className="relative z-10 flex-1 min-w-0 p-3 sm:p-4 md:p-6 brand-tiles">
          {usageError && <p role="status" className="text-xs text-amber-800 mb-2">{usageError}</p>}
          {isOffline && (
            <div
              role="status"
              aria-live="polite"
              className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
            >
              Your device reports no internet connection. Changes may not reach Firebase; wait for connectivity and confirm each save before leaving.
            </div>
          )}
          <Suspense fallback={<LoadingFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex mobile-navigation" role="dialog" aria-modal="true" aria-label="Main navigation">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div className="relative z-10 h-full">
            <Sidebar variant="mobile" onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const portalRedirect = portalDomainRedirect(window.location.hostname, location);
  if (portalRedirect) return <Navigate to={portalRedirect} replace />;
  console.log("App rendering");
  
  return (
    <ThemeProvider>
      <BrandProvider>
        <AuthProvider>
          {firebaseEmulatorsEnabled && (
            <div className="fixed bottom-2 right-2 z-[100] rounded bg-amber-300 px-3 py-2 text-xs font-bold text-black shadow">
              LOCAL FIREBASE EMULATORS — TEST DATA ONLY
            </div>
          )}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/portal" element={customerPortalEnabled ? <Suspense fallback={<LoadingFallback publicPage />}><CustomerPortal /></Suspense> : <div className="p-6">My Account customer portal is not enabled yet. Contact Alkeya Mobile at <a className="underline" href="mailto:support@alkeya.me">support@alkeya.me</a> for account assistance.</div>} />
            {/* Public routes (no auth, no sidebar) */}
            <Route path="/public/inventory" element={<Suspense fallback={<LoadingFallback publicPage />}><PublicInventory /></Suspense>} />
            <Route path="/public/repair" element={<Navigate to="/portal#account-repairs" replace />} />
            {/* Alias for plural path */}
            <Route path="/public/repairs" element={<Navigate to="/portal#account-repairs" replace />} />
            <Route path="/travel-install" element={<Suspense fallback={<LoadingFallback publicPage />}><TravelInstall /></Suspense>} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>} />
              <Route path="/dashboard" element={<Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>} />
              <Route path="/customers" element={<Suspense fallback={<LoadingFallback />}><Customers /></Suspense>} />
              <Route path="/customers/:id" element={<Suspense fallback={<LoadingFallback />}><CustomerDetail /></Suspense>} />
              <Route path="/repairs" element={<Suspense fallback={<LoadingFallback />}><Repairs /></Suspense>} />
              <Route path="/lines" element={<Suspense fallback={<LoadingFallback />}><Lines /></Suspense>} />
              <Route path="/service-requests" element={<Suspense fallback={<LoadingFallback />}><ServiceRequests /></Suspense>} />
              <Route path="/travel-esims" element={<Suspense fallback={<LoadingFallback />}><TravelEsims /></Suspense>} />
              <Route path="/plans" element={<Suspense fallback={<LoadingFallback />}><PlanTemplates /></Suspense>} />
              <Route path="/reminders" element={<Suspense fallback={<LoadingFallback />}><Reminders /></Suspense>} />
              <Route path="/inventory" element={<Suspense fallback={<LoadingFallback />}><Inventory /></Suspense>} />
              <Route path="/payments" element={<Suspense fallback={<LoadingFallback />}><Payments /></Suspense>} />
              <Route path="/shipments" element={<Suspense fallback={<LoadingFallback />}><Shipments /></Suspense>} />
              <Route path="/ledger" element={<Suspense fallback={<LoadingFallback />}><Ledger /></Suspense>} />
              <Route path="/purchase-orders" element={<Suspense fallback={<LoadingFallback />}><PurchaseOrders /></Suspense>} />
              <Route path="/financial-report" element={<Suspense fallback={<LoadingFallback />}><FinancialReport /></Suspense>} />
              <Route path="/rewards" element={<Suspense fallback={<LoadingFallback />}><RewardsEnhanced /></Suspense>} />
              <Route path="/messages" element={<Suspense fallback={<LoadingFallback />}><Messages /></Suspense>} />
              <Route path="/crm" element={<Suspense fallback={<LoadingFallback />}><CrmInbox /></Suspense>} />
              <Route path="/vendor-inventory" element={<Suspense fallback={<LoadingFallback />}><VendorInventory /></Suspense>} />
              <Route path="/mobile-sentrix" element={<Suspense fallback={<LoadingFallback />}><MobileSentrix /></Suspense>} />
              <Route path="/parts" element={<Suspense fallback={<LoadingFallback />}><PartsInventory /></Suspense>} />
              <Route path="/finance" element={<Suspense fallback={<LoadingFallback />}><Finance /></Suspense>} />
              <Route path="/quotes" element={<Suspense fallback={<LoadingFallback />}><Quotes /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<LoadingFallback />}><AdminCenter /></Suspense>} />
              <Route path="/promotions" element={<Suspense fallback={<LoadingFallback />}><Promotions /></Suspense>} />
              <Route path="/profitability" element={<Suspense fallback={<LoadingFallback />}><Profitability /></Suspense>} />
              <Route path="/collections" element={<Suspense fallback={<LoadingFallback />}><Collections /></Suspense>} />
              <Route path="/financial-ledger" element={<Suspense fallback={<LoadingFallback />}><FinancialLedger /></Suspense>} />
              <Route path="/carrier-costs" element={<Suspense fallback={<LoadingFallback />}><CarrierCosts /></Suspense>} />
              <Route path="/trade-ins" element={<Navigate to="/quotes?tab=receipts" replace />} />
              <Route path="/admin/center" element={<LegacyAdminRedirect />} />
              <Route path="/admin/branding" element={<LegacyAdminRedirect branding />} />
              <Route path="/account" element={<Suspense fallback={<LoadingFallback />}><Account /></Suspense>} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrandProvider>
    </ThemeProvider>
  );
}
